import { motion, useInView } from "framer-motion";
import { useEffect, useRef, useState } from "react";

const ThesisSection = () => {
    return (
        <section data-testid="thesis-section" className="relative py-24 md:py-32 border-t border-[var(--border)] overflow-hidden">
            <div className="max-w-[1280px] mx-auto px-6 md:px-10 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-14 items-center">
                <motion.div
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true, margin: "-80px" }}
                    transition={{ duration: 0.8 }}
                    className="lg:col-span-5"
                >
                    <span className="eyebrow-pill">Our thesis</span>
                    <h2 className="font-display text-[clamp(2rem,4.2vw,3.2rem)] leading-[1.04] tracking-tight font-semibold text-[var(--ink)] mt-5 mb-6">
                        Context is in the{" "}
                        <span className="accent-blue">conversations.</span>
                    </h2>
                    <p className="text-lg text-[var(--ink-soft)] leading-relaxed mb-5">
                        Every call carries the facts that move a case — the injury, the insurer, the dates, the doubts.
                        Lexafide listens, pulls them out, and weaves them into a living{" "}
                        <span className="accent-blue font-medium">case context</span> your AI agents share.
                    </p>
                    <p className="text-lg text-[var(--ink)] leading-relaxed">
                        Every next conversation is{" "}
                        <span className="font-semibold accent-blue">sharper than the one before.</span>
                    </p>

                    <ul className="mt-8 space-y-3.5">
                        {[
                            "Listens across voice, text, and email",
                            "Extracts key facts as they're said",
                            "Weaves them into a living case-context web",
                            "Primes every next AI conversation, automatically",
                        ].map((s, i) => (
                            <motion.li
                                key={s}
                                initial={{ opacity: 0, x: -8 }}
                                whileInView={{ opacity: 1, x: 0 }}
                                viewport={{ once: true }}
                                transition={{ duration: 0.5, delay: 0.2 + i * 0.08 }}
                                className="flex items-center gap-3 text-[var(--ink)]"
                            >
                                <span className="w-1.5 h-1.5 rounded-full bg-[var(--blue)] flex-shrink-0" />
                                <span>{s}</span>
                            </motion.li>
                        ))}
                    </ul>
                </motion.div>

                <motion.div
                    initial={{ opacity: 0, scale: 0.96 }}
                    whileInView={{ opacity: 1, scale: 1 }}
                    viewport={{ once: true, margin: "-80px" }}
                    transition={{ duration: 0.9 }}
                    className="lg:col-span-7 relative"
                >
                    <TranscriptToWeb />
                </motion.div>
            </div>
        </section>
    );
};

/* ---------- Transcript → spider-web visualization ---------- */

// Each fact corresponds to one highlighted phrase in the transcript AND one node in the web.
// `phrase` is the substring inside the transcript to highlight. `label` is the node label.
const FACTS = [
    { id: "incident", label: "Incident",  phrase: "rear-ended on the 405", node: { x: 50, y: 14 } },
    { id: "date",     label: "Date",      phrase: "last Tuesday",          node: { x: 18, y: 32 } },
    { id: "insurer",  label: "Insurer",   phrase: "Acme",                  node: { x: 82, y: 32 } },
    { id: "provider", label: "Provider",  phrase: "St. Mary's",            node: { x: 10, y: 62 } },
    { id: "records",  label: "Records",   phrase: "X-rays",                node: { x: 90, y: 62 } },
    { id: "symptom",  label: "Symptom",   phrase: "back is killing me",    node: { x: 28, y: 86 } },
    { id: "claim",    label: "Claim #",   phrase: "4471",                  node: { x: 72, y: 86 } },
];

const TRANSCRIPT = `"Hi, I'm calling about my accident. I was rear-ended on the 405 last Tuesday. The other driver — their insurance is Acme. I was taken to St. Mary's by ambulance, they did X-rays. Now my back is killing me. The claim number they gave me is 4471."`;

const TranscriptToWeb = () => {
    const ref = useRef(null);
    const inView = useInView(ref, { once: false, margin: "-100px" });
    const [activeIdx, setActiveIdx] = useState(-1); // -1 = nothing highlighted yet

    // Step through facts one at a time when in view; pause at the end, then loop
    useEffect(() => {
        if (!inView) return;
        let mounted = true;
        const run = async () => {
            // brief delay so the section enters first
            await new Promise((r) => setTimeout(r, 600));
            while (mounted) {
                for (let i = 0; i < FACTS.length; i++) {
                    if (!mounted) return;
                    setActiveIdx(i);
                    await new Promise((r) => setTimeout(r, 1100));
                }
                // Hold the full web for a beat
                await new Promise((r) => setTimeout(r, 2200));
                if (!mounted) return;
                setActiveIdx(-1);
                await new Promise((r) => setTimeout(r, 800));
            }
        };
        run();
        return () => { mounted = false; };
    }, [inView]);

    const captured = activeIdx + 1;
    return (
        <div ref={ref} data-testid="thesis-transcript-web" className="grid grid-cols-1 md:grid-cols-[1fr_auto_1fr] gap-3 md:gap-2 items-start">
            <Transcript activeIdx={activeIdx} />
            <RightConnector />
            <ContextWeb activeIdx={activeIdx} captured={captured} />
        </div>
    );
};

/* ---------- Transcript card ---------- */
const Transcript = ({ activeIdx }) => {
    // Split the transcript into segments, marking which segments correspond to facts
    const segments = buildSegments(TRANSCRIPT, FACTS);
    return (
        <div className="card-paper p-4 md:p-5">
            <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2.5">
                    <div className="w-8 h-8 rounded-full bg-[var(--blue-soft)] flex items-center justify-center">
                        <i className="fa-solid fa-phone-volume text-[var(--blue)] text-[12px]" />
                    </div>
                    <div className="leading-tight">
                        <div className="font-display font-semibold text-[var(--ink)] text-[13px]">Jane Doe</div>
                        <div className="font-mono text-[9px] tracking-wider uppercase text-[var(--ink-dim)]">
                            Inbound call · 9:42 AM
                        </div>
                    </div>
                </div>
                <span className="relative inline-flex items-center gap-1.5 font-mono text-[9px] tracking-[0.18em] uppercase text-[#16A34A] font-semibold">
                    <span className="relative inline-flex w-1.5 h-1.5">
                        <span className="absolute inset-0 rounded-full bg-[#16A34A] animate-ping opacity-70" />
                        <span className="relative inline-flex w-1.5 h-1.5 rounded-full bg-[#16A34A]" />
                    </span>
                    Listening · live
                </span>
            </div>

            <p className="text-[13.5px] text-[var(--ink)] leading-relaxed font-serif italic">
                {segments.map((seg, i) => {
                    if (seg.factIdx === undefined) return <span key={`s-${i}`}>{seg.text}</span>;
                    const isActive = activeIdx === seg.factIdx;
                    const isCaptured = activeIdx > seg.factIdx;
                    return (
                        <motion.span
                            key={`f-${seg.factIdx}-${i}`}
                            animate={{
                                background: isActive
                                    ? "rgba(30,91,255,0.22)"
                                    : isCaptured
                                    ? "rgba(30,91,255,0.10)"
                                    : "rgba(30,91,255,0)",
                                color: isActive || isCaptured ? "var(--blue-deep)" : "var(--ink)",
                            }}
                            transition={{ duration: 0.35 }}
                            className="rounded px-1 py-0.5 font-medium not-italic"
                            style={{ fontStyle: "normal" }}
                        >
                            {seg.text}
                        </motion.span>
                    );
                })}
            </p>
        </div>
    );
};

const buildSegments = (text, facts) => {
    // Find each phrase in order of appearance and split text into segments
    let cursor = 0;
    const segments = [];
    facts.forEach((f, idx) => {
        const pos = text.indexOf(f.phrase, cursor);
        if (pos === -1) return;
        if (pos > cursor) segments.push({ text: text.slice(cursor, pos) });
        segments.push({ text: f.phrase, factIdx: idx });
        cursor = pos + f.phrase.length;
    });
    if (cursor < text.length) segments.push({ text: text.slice(cursor) });
    return segments;
};

/* ---------- Connector between transcript and web ---------- */
/* Desktop (side-by-side): horizontal arrow pointing RIGHT */
/* Mobile (stacked):       vertical arrow pointing DOWN */
const RightConnector = () => (
    <div className="flex md:flex-col items-center justify-center gap-2 px-2 md:px-0 md:w-10 md:self-center md:mt-12">
        {/* Mobile: down arrow */}
        <svg width="14" height="36" viewBox="0 0 14 36" className="md:hidden">
            <motion.line
                x1="7" y1="0" x2="7" y2="30"
                stroke="var(--blue)"
                strokeWidth="1.2"
                strokeDasharray="3 3"
                animate={{ strokeDashoffset: [0, -6] }}
                transition={{ duration: 0.7, repeat: Infinity, ease: "linear" }}
            />
            <path d="M 3 30 L 7 36 L 11 30" fill="none" stroke="var(--blue)" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
        {/* Desktop: right arrow */}
        <svg width="36" height="14" viewBox="0 0 36 14" className="hidden md:block">
            <motion.line
                x1="0" y1="7" x2="30" y2="7"
                stroke="var(--blue)"
                strokeWidth="1.2"
                strokeDasharray="3 3"
                animate={{ strokeDashoffset: [0, -6] }}
                transition={{ duration: 0.7, repeat: Infinity, ease: "linear" }}
            />
            <path d="M 30 3 L 36 7 L 30 11" fill="none" stroke="var(--blue)" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
        <span className="font-mono text-[8px] tracking-[0.2em] uppercase text-[var(--blue-deep)]/70 font-semibold whitespace-nowrap md:[writing-mode:vertical-rl] md:rotate-180">
            Pulled in
        </span>
    </div>
);

/* ---------- The spider web of context ---------- */
const ContextWeb = ({ activeIdx, captured }) => {
    const cx = 50, cy = 50;
    return (
        <div className="card-paper p-4 md:p-5 relative">
            <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2.5">
                    <div className="w-8 h-8 rounded-xl bg-[var(--blue)] flex items-center justify-center">
                        <i className="fa-solid fa-diagram-project text-white text-[12px]" />
                    </div>
                    <div className="leading-tight">
                        <div className="font-display font-semibold text-[var(--ink)] text-[13px]">
                            Case context · web
                        </div>
                        <div className="font-mono text-[9px] tracking-wider uppercase text-[var(--ink-dim)]">
                            Case #4471 · Jane Doe
                        </div>
                    </div>
                </div>
                <div className="text-right leading-tight">
                    <div className="font-display font-semibold text-[var(--blue-deep)] text-base tabular-nums">
                        {captured}/{FACTS.length}
                    </div>
                    <div className="font-mono text-[8px] tracking-wider uppercase text-[var(--ink-dim)]">
                        facts woven
                    </div>
                </div>
            </div>

            <div className="relative aspect-square w-full">
                {/* Background concentric web "strands" — purely decorative spider-web texture */}
                <svg viewBox="0 0 100 100" className="absolute inset-0 w-full h-full" preserveAspectRatio="none">
                    {/* concentric rings */}
                    {[24, 36, 46].map((r) => (
                        <ellipse
                            key={r}
                            cx={cx} cy={cy}
                            rx={r} ry={r}
                            fill="none"
                            stroke="rgba(30,91,255,0.08)"
                            strokeWidth="0.25"
                        />
                    ))}
                    {/* radial spokes */}
                    {FACTS.map((f, i) => (
                        <line
                            key={`spoke-${i}`}
                            x1={cx} y1={cy} x2={f.node.x} y2={f.node.y}
                            stroke="rgba(30,91,255,0.10)"
                            strokeWidth="0.25"
                            strokeDasharray="0.7 0.7"
                        />
                    ))}
                    {/* "active" connection edges — only drawn for captured facts */}
                    {FACTS.map((f, i) => {
                        const isCaptured = i <= activeIdx;
                        const isActive = i === activeIdx;
                        return (
                            <motion.line
                                key={`edge-${i}`}
                                x1={cx} y1={cy} x2={f.node.x} y2={f.node.y}
                                stroke={isCaptured ? "var(--blue)" : "transparent"}
                                strokeWidth={isActive ? "0.7" : "0.45"}
                                strokeOpacity={isCaptured ? (isActive ? 1 : 0.7) : 0}
                                animate={{
                                    strokeOpacity: isCaptured ? (isActive ? 1 : 0.7) : 0,
                                }}
                                transition={{ duration: 0.4 }}
                            />
                        );
                    })}
                </svg>

                {/* Center: Case node */}
                <div
                    className="absolute -translate-x-1/2 -translate-y-1/2 flex items-center justify-center"
                    style={{ left: `${cx}%`, top: `${cy}%` }}
                >
                    <motion.div
                        animate={{
                            scale: activeIdx >= 0 ? 1 : 0.92,
                            boxShadow: `0 0 0 ${3 + Math.min(captured, 7) * 1.5}px rgba(30,91,255,0.07), 0 8px 22px -6px rgba(30,91,255,0.4)`,
                        }}
                        transition={{ duration: 0.5 }}
                        className="rounded-full bg-[var(--blue)] flex items-center justify-center px-3 py-2 border-2 border-white"
                    >
                        <div className="text-center">
                            <div className="font-mono text-[7px] tracking-[0.15em] uppercase text-white/70 leading-none">
                                Case
                            </div>
                            <div className="font-display font-bold text-white text-[12px] leading-tight">
                                #4471
                            </div>
                        </div>
                    </motion.div>
                </div>

                {/* Fact nodes */}
                {FACTS.map((f, i) => {
                    const isCaptured = i <= activeIdx;
                    const isActive = i === activeIdx;
                    return (
                        <motion.div
                            key={f.id}
                            animate={{
                                scale: isCaptured ? 1 : 0.75,
                                opacity: isCaptured ? 1 : 0.3,
                            }}
                            transition={{ duration: 0.45, ease: [0.16, 1, 0.3, 1] }}
                            className="absolute -translate-x-1/2 -translate-y-1/2"
                            style={{ left: `${f.node.x}%`, top: `${f.node.y}%` }}
                        >
                            <motion.div
                                animate={{
                                    background: isCaptured ? "var(--paper)" : "var(--cream-2)",
                                    borderColor: isActive ? "var(--blue)" : isCaptured ? "rgba(30,91,255,0.35)" : "var(--border)",
                                    boxShadow: isActive
                                        ? "0 0 0 3px rgba(30,91,255,0.15), 0 6px 14px -5px rgba(30,91,255,0.35)"
                                        : isCaptured
                                        ? "0 3px 8px -3px rgba(35,31,35,0.15)"
                                        : "none",
                                }}
                                transition={{ duration: 0.35 }}
                                className="border rounded-full px-2 py-0.5 flex items-center gap-1.5 whitespace-nowrap"
                            >
                                <motion.span
                                    animate={{ background: isCaptured ? "var(--blue)" : "var(--border-strong)" }}
                                    className="w-3 h-3 rounded-full flex items-center justify-center flex-shrink-0"
                                >
                                    {isCaptured && <i className="fa-solid fa-check text-white text-[6px]" />}
                                </motion.span>
                                <span className={`font-mono text-[8px] tracking-wider uppercase font-semibold ${
                                    isCaptured ? "text-[var(--blue-deep)]" : "text-[var(--ink-dim)]"
                                }`}>
                                    {f.label}
                                </span>
                            </motion.div>
                        </motion.div>
                    );
                })}
            </div>

            {/* Footer hint */}
            <div className="mt-3 flex items-center justify-between text-[9px] font-mono tracking-wider uppercase text-[var(--ink-dim)]">
                <span>
                    <i className="fa-solid fa-bolt text-[var(--blue)] mr-1" />
                    Context primed for every next AI conversation
                </span>
                <span className="accent-blue">CRM in sync ✓</span>
            </div>
        </div>
    );
};

export default ThesisSection;
